import {
  DynamicDrawUsage,
  InstancedBufferAttribute,
  InstancedMesh,
  Matrix4,
  type Material,
} from 'three';
import type { LinearRGB } from '../../core/color';
import type { Rng } from '../../core/rng';
import type { Vec3 } from '../../geo/types';
import { edgeIdFor, vesselEdgeIdFor } from '../mesh/attributes';
import type { Animated, NightLight } from '../types';
import type { FormedTemplate } from './shipForm';
import { buildShipLantern, lanternFlame } from './shipLantern';
import { litShips, shipLight, type LitShip } from './shipLights';
import { shipInstanceOf } from './shipMeshes';
import { writeShipPose, type ShipInstance } from './shipMotion';
import { painterScaleOf, type PlacedShip } from './shipPlacement';
import { SHIP_TEMPLATES, type ShipType } from './shipTemplates';

/*
 * The lanterns of the lit ships (shipLantern.ts): one instanced mesh a type of ship, each lantern
 * in its ship's own frame and pose, so that it rocks with the hull and grows with it in the
 * painter view, and the light each burns, which follows the candle from frame to frame.
 */
export interface ShipLanterns {
  readonly meshes: readonly InstancedMesh[];
  readonly animated: Animated;
}

/* The lanterns of one type of ship: their mesh and, by slot, each ship's pose and light. */
interface Row {
  readonly mesh: InstancedMesh;
  readonly flame: Vec3;
  readonly ships: readonly { readonly lit: LitShip; readonly instance: ShipInstance }[];
}

function rowOf(
  type: ShipType,
  ships: Row['ships'],
  wood: LinearRGB,
  material: Material,
  order: number
): Row {
  const template = SHIP_TEMPLATES[type] as FormedTemplate;
  const geometry = buildShipLantern(template, wood, edgeIdFor(order), order);
  const scales = ships.flatMap(() => painterScaleOf(type));
  const ids = ships.map(({ lit }) => vesselEdgeIdFor(lit.index));
  geometry.setAttribute(
    'aPainterScale',
    new InstancedBufferAttribute(Float32Array.from(scales), 2)
  );
  geometry.setAttribute('aInstanceEdgeId', new InstancedBufferAttribute(Float32Array.from(ids), 1));
  const mesh = new InstancedMesh(geometry, material, ships.length);
  /* A fleet mesh is known by the type its name begins with (shipsFeature.real.test.ts). */
  mesh.name = `${type} lanterns`;
  mesh.userData['lanternsOf'] = ships.map(({ lit }) => lit.ship.id);
  mesh.frustumCulled = false;
  mesh.instanceMatrix.setUsage(DynamicDrawUsage);
  return { mesh, flame: lanternFlame(template), ships };
}

/* A point of the ship's frame in the world, under the pose `te` holds. */
function posed(te: Matrix4['elements'], [x, y, z]: Vec3): Vec3 {
  return [
    te[0] * x + te[4] * y + te[8] * z + te[12],
    te[1] * x + te[5] * y + te[9] * z + te[13],
    te[2] * x + te[6] * y + te[10] * z + te[14],
  ];
}

/** A lantern on every lit ship of `ships`, and the lights that burn in them. */
export function createShipLanterns(
  ships: readonly PlacedShip[],
  wood: LinearRGB,
  material: Material,
  rng: Rng
): ShipLanterns {
  const lit = litShips(ships, rng.fork('lights')).map((entry) => ({
    lit: entry,
    instance: shipInstanceOf(entry.ship, entry.index, rng),
  }));
  const types = [...new Set(lit.map((entry) => entry.lit.ship.type))].sort();
  const rows = types.map((type, order) =>
    rowOf(
      type,
      lit.filter((entry) => entry.lit.ship.type === type),
      wood,
      material,
      order
    )
  );
  const matrix = new Matrix4();
  const lights = new Map<LitShip, NightLight>();
  const update = (timeSeconds: number): void => {
    for (const row of rows) {
      row.ships.forEach((ship, slot) => {
        writeShipPose(matrix.elements, ship.instance, timeSeconds);
        row.mesh.setMatrixAt(slot, matrix);
        lights.set(ship.lit, shipLight(ship.lit, posed(matrix.elements, row.flame)));
      });
      row.mesh.instanceMatrix.needsUpdate = true;
    }
  };
  update(0);
  return {
    meshes: rows.map((row) => row.mesh),
    animated: {
      id: 'ship-lanterns',
      update,
      lights: { moving: true, list: () => lit.map((entry) => lights.get(entry.lit)!) },
    },
  };
}
